import { Box, Chip, Link, Stack, Typography } from '@mui/joy';
import dayjs from 'dayjs';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SlidingPanel } from 'core/components';
import { DB_DATE_FORMAT, DEFAULT_PLACEHOLDER } from 'core/constants';
import { Loader } from 'core/elements';
import { LinkTarget } from 'core/enums';
import { formatDate } from 'core/utils';
import { useGetVendorQuoteImportAttemptLog } from 'dashboard/hooks';
import { IVendorQuoteImportAttempt } from 'dashboard/types';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { getCompanyLabel, getImportErrorLabel, getImportFileDownloadUrl } from './utils';

interface IProps {
  attempt: IVendorQuoteImportAttempt;
  isOpen: boolean;
  onClose: () => void;
}

interface IDetailProps {
  label: string;
  // ReactNode, not just text: the file name renders as a download link while
  // every other detail is plain text, and one row component beats two.
  value?: ReactNode;
}

const Detail = ({ label, value }: IDetailProps) => (
  <Stack sx={{ gap: 0.25, minWidth: 160 }}>
    <Typography level={Level.BODY_XS} sx={{ color: 'neutral.500' }}>
      {label}
    </Typography>
    <Typography level={Level.BODY_SM}>{value ?? DEFAULT_PLACEHOLDER}</Typography>
  </Stack>
);

export const ImportDetailsPanel = ({ attempt, isOpen, onClose }: IProps) => {
  const { t } = useTranslation();

  // The log is written per day, so the lookup only needs the days the attempt
  // spans — a retry can land past midnight, hence the finished date too.
  const requestPayload = useMemo(() => {
    // An attempt with no start timestamp still has a finish one far more often
    // than not; using today for it would ask for a day that was never this
    // upload's and then report that nothing was logged.
    const firstKnownDate = attempt?.startedAt || attempt?.finishedAt;

    const startedAt = firstKnownDate ? dayjs(firstKnownDate) : dayjs();
    const finishedAt = attempt?.finishedAt ? dayjs(attempt.finishedAt) : startedAt;

    return {
      dateFrom: startedAt.format(DB_DATE_FORMAT),
      dateTo: finishedAt.format(DB_DATE_FORMAT),
      documentId: attempt?.documentId,
      // The row id is the correlation guid, except on uploads old enough to
      // predate it — the BE ignores anything that is not a guid.
      guid: attempt?.id,
    };
  }, [attempt]);

  const { attemptLog, isLoading, isError } = useGetVendorQuoteImportAttemptLog(requestPayload, {
    enabled: isOpen && !!attempt,
  });

  const entries = attemptLog?.entries ?? [];

  const errorLabel = getImportErrorLabel(attempt);

  const downloadUrl = getImportFileDownloadUrl(attempt?.fileId);

  // Falls back to plain text when the attempt was never tied to an import, so
  // there is no file to fetch — same rule the grid's File Name cell applies.
  const fileNameValue = downloadUrl ? (
    <Link href={downloadUrl} target={LinkTarget.BLANK} level={Level.BODY_SM}>
      {attempt?.fileName || t('labelDownload')}
    </Link>
  ) : (
    attempt?.fileName
  );

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onClose}
      size={Size.LG}
      title={t('titleImportDetails', { importId: attempt?.importId ?? DEFAULT_PLACEHOLDER })}
      subtitle={attempt?.fileName ?? DEFAULT_PLACEHOLDER}
    >
      <Stack sx={{ gap: 2 }}>
        <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 2 }}>
          <Detail label={t('headerDocumentId')} value={attempt?.documentId} />
          <Detail label={t('headerVendorQuoteId')} value={attempt?.quoteId} />
          <Detail
            label={t('headerCustomerQuoteIds')}
            value={attempt?.customerQuoteIds?.join(', ') || null}
          />
          <Detail label={t('headerCompany')} value={getCompanyLabel(attempt)} />
          <Detail label={t('headerFileName')} value={fileNameValue} />
          <Detail label={t('headerUser')} value={attempt?.userName} />
          <Detail label={t('headerDateTime')} value={formatDate(attempt?.startedAt, true)} />
          <Detail label={t('headerParts')} value={attempt?.partCount} />
          <Detail label={t('headerImportedParts')} value={attempt?.importedPartCount} />
          <Detail label={t('headerDiscountItems')} value={attempt?.discountCount} />
        </Stack>

        {!!errorLabel && (
          <Box>
            <Typography level={Level.BODY_XS} sx={{ color: 'neutral.500' }}>
              {t('headerError')}
            </Typography>
            <Typography level={Level.BODY_SM} color={Color.DANGER}>
              {errorLabel}
            </Typography>
          </Box>
        )}

        <Stack sx={{ gap: 1 }}>
          <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
            <Typography level={Level.TITLE_SM}>{t('labelImportLogTrace')}</Typography>
            {!isLoading && !isError && (
              <Chip variant={Variant.SOFT} color={Color.NEUTRAL}>
                {t('labelEntryCount', { count: entries.length })}
              </Chip>
            )}
          </Stack>

          {isLoading && <Loader size={Size.SM} />}

          {isError && (
            <Typography level={Level.BODY_SM} color={Color.DANGER}>
              {t('errorImportLogTrace')}
            </Typography>
          )}

          {!isLoading && !isError && entries.length === 0 && (
            <Typography level={Level.BODY_SM} sx={{ color: 'neutral.500' }}>
              {t('labelNoImportLogEntries')}
            </Typography>
          )}

          {entries.map((entry, index) => (
            // Timestamps repeat within a second, so they are not unique on
            // their own; the index keeps sibling entries distinct.
            <Box
              key={`${entry.timestamp}-${index}`}
              sx={{
                backgroundColor: 'neutral.50',
                border: '1px solid',
                borderColor: 'neutral.200',
                borderRadius: 'sm',
                padding: 1,
              }}
            >
              <Typography
                component="pre"
                level={Level.BODY_XS}
                // Wrap rather than clip: raw entries carry the serialized
                // payload, so `pre` gave every line its own horizontal
                // scrollbar — and those scroll containers take no focus, so a
                // keyboard user could not reach the rest of any line.
                sx={{
                  fontFamily: 'monospace',
                  margin: 0,
                  overflowWrap: 'anywhere',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {entry.raw}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    </SlidingPanel>
  );
};
